import { useState, useMemo, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, User, Copy, Check, AlertTriangle } from 'lucide-react';
import {
  useShadowAiTools,
  useRunningProcesses,
  useShadowAiOrgStats,
} from '@/hooks/api/useShadowAiTools';
import { useMembers } from '@/hooks/api/useMembers';
import { TABLE_IDS } from '@/components/DataTable/tableIds';
import { getInstalledToolRowId } from '@/components/shared/shadow-ai';

import { SummaryStats } from '@/pages/dashboard/tabs/ShadowAITab/components';
import { DataTableRunningNowContent } from './DataTableRunningNowTab';
import { InstalledToolsSection } from './InstalledToolsSection';
import { DataTableBrowserAiToolsContent } from './DataTableBrowserAiToolsTab';
import { isShadowAiScopePerson } from '@/lib/member-kinds';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';

function CopyButton({ text }: { text: string }): ReactElement {
  const [copied, setCopied] = useState(false);

  const handleCopy = (): void => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-0.5 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground" />
      )}
    </button>
  );
}

function UserComparisonBanner({
  userToolCount,
  orgTotalTools,
  memberCount,
}: {
  userToolCount: number;
  orgTotalTools: number;
  memberCount: number;
}): ReactElement | null {
  if (memberCount === 0 || userToolCount === 0) return null;

  const avgToolsPerUser = orgTotalTools / memberCount;
  if (avgToolsPerUser === 0) return null;

  const ratio = userToolCount / avgToolsPerUser;
  if (ratio < 2) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <span className="text-sm text-amber-800 dark:text-amber-200">
          This user has <strong>{ratio.toFixed(1)}x</strong> more AI tools than
          the organization average ({avgToolsPerUser.toFixed(1)} per user)
        </span>
      </div>
    </div>
  );
}

function UserScopeBanner({
  userName,
  userEmail,
  userId,
  onBack,
}: {
  userName: string | null;
  userEmail: string | null;
  userId: string;
  onBack: () => void;
}): ReactElement {
  return (
    <div className="sticky top-16 z-20 -mx-6 px-6 py-3 bg-blue-50 dark:bg-blue-950 border-b border-blue-200 dark:border-blue-800">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="gap-2 border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Organization
          </Button>
          <div className="h-4 w-px bg-blue-200 dark:bg-blue-800" />
          <div className="flex items-center gap-3">
            <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="font-medium text-blue-700 dark:text-blue-300">
              {userName ?? 'Unknown User'}
            </span>
            {userEmail && (
              <>
                <div className="h-4 w-px bg-blue-200 dark:bg-blue-800" />
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">
                    {userEmail}
                  </span>
                  <CopyButton text={userEmail} />
                </div>
              </>
            )}
            <div className="h-4 w-px bg-blue-200 dark:bg-blue-800" />
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground/70 font-mono">
                {userId}
              </span>
              <CopyButton text={userId} />
            </div>
          </div>
        </div>
        <Badge className="bg-blue-600 hover:bg-blue-700 text-white">
          User Scope
        </Badge>
      </div>
    </div>
  );
}

/**
 * Counting-only page size. The three tables below own their own pagination;
 * these queries exist purely to feed the summary widgets a total, so they ask
 * for the smallest page the API allows.
 */
const COUNT_ONLY_LIMIT = 1;

/** Where a drill-down link came from; also which table it points at. */
type DeepLinkSource = 'browser' | 'installed' | 'running';

const DEEP_LINK_SOURCES: DeepLinkSource[] = ['browser', 'installed', 'running'];

function parseDeepLinkSource(value: string | null): DeepLinkSource | null {
  return DEEP_LINK_SOURCES.find(s => s === value) ?? null;
}

export function UserDrillDown({
  organizationId,
  userId,
  onBack,
}: {
  organizationId: string;
  userId: string;
  onBack: () => void;
}): ReactElement {
  const [searchParams] = useSearchParams();

  const userName = searchParams.get('name');
  const userEmail = searchParams.get('email');

  // KIR-3495: a deep link from the org tables names one tool and which table it
  // came from. The converted tables have no transient row highlight, so seed
  // the row as expanded instead — the DataTable scrolls to it and rings it.
  //
  // `source` is validated, not cast: `handleRunningNowUserClick` writes
  // `source=running`, and a cast would let that through as a lie and silently
  // land the user on the wrong tab.
  const deepLinkedTool = searchParams.get('tool');
  const deepLinkedSource = parseDeepLinkSource(searchParams.get('source'));
  // Running Now rows are keyed by the process id, which the org table passes
  // as `process` (not `tool`).
  const deepLinkedProcess = searchParams.get('process');
  // Installed rows are keyed by `${toolId}::${category}` (getInstalledToolRowId),
  // so a bare toolId can never match. The org table passes the category as
  // `category`; without it there is nothing to expand.
  const deepLinkedCategory = searchParams.get('category');

  const installedInitialState = useMemo(
    () =>
      deepLinkedSource === 'installed' && deepLinkedTool && deepLinkedCategory
        ? {
            expandedRowIds: new Set([
              getInstalledToolRowId({
                toolId: deepLinkedTool,
                category: deepLinkedCategory,
              }),
            ]),
          }
        : undefined,
    [deepLinkedSource, deepLinkedTool, deepLinkedCategory]
  );

  // Browser rows open a detail dialog rather than expanding, so a deep link is
  // honoured by opening that dialog — seeding `expandedRowIds` would be inert
  // on a table with row expansion disabled.
  const deepLinkedDomain =
    deepLinkedSource === 'browser' && deepLinkedTool
      ? deepLinkedTool
      : undefined;

  const runningInitialState = useMemo(
    () =>
      deepLinkedSource === 'running' && deepLinkedProcess
        ? { expandedRowIds: new Set([deepLinkedProcess]) }
        : undefined,
    [deepLinkedSource, deepLinkedProcess]
  );

  const { data: statsData } = useShadowAiOrgStats(organizationId);
  const { data: members = [] } = useMembers(organizationId);
  const { flags } = useFeatureFlags([flagsmithFeatureFlags.SHARED_DEVICES]);
  const sharedDevicesEnabled =
    flags[flagsmithFeatureFlags.SHARED_DEVICES] ?? false;

  // Widget totals only — deliberately unfiltered, so the per-column filters on
  // the tables below never move these numbers (KIR-3495: "widgets unchanged").
  const { data: installedCounts, error } = useShadowAiTools(organizationId, {
    page: 1,
    limit: COUNT_ONLY_LIMIT,
    sourceType: 'installed',
    userId,
  });

  const { data: browserCounts } = useShadowAiTools(organizationId, {
    page: 1,
    limit: COUNT_ONLY_LIMIT,
    sourceType: 'browser',
    userId,
  });

  const { data: runningCounts } = useRunningProcesses(organizationId, {
    page: 1,
    limit: COUNT_ONLY_LIMIT,
    userId,
  });

  const installedTotal = installedCounts?.pagination?.total ?? 0;
  const browserTotal = browserCounts?.pagination?.total ?? 0;
  const runningTotal = runningCounts?.pagination?.total ?? 0;

  // All three tables are stacked and always mounted, so a deep-linked row is
  // seeded once at mount and never re-applied — no tab remount to guard.

  if (error) {
    return (
      <div className="space-y-4">
        <UserScopeBanner
          userName={userName}
          userEmail={userEmail}
          userId={userId}
          onBack={onBack}
        />
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-destructive">
              Failed to load user data. Please try again.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <UserScopeBanner
        userName={userName}
        userEmail={userEmail}
        userId={userId}
        onBack={onBack}
      />

      <UserComparisonBanner
        userToolCount={installedTotal + browserTotal}
        orgTotalTools={statsData?.totalTools ?? 0}
        // Same people as the backend Shadow AI scope: current accounts, plus
        // Discovered/Unattributed with shared devices on (KIR-3908, KIR-5410).
        memberCount={
          members.filter(m => isShadowAiScopePerson(m, sharedDevicesEnabled))
            .length
        }
      />

      <SummaryStats
        runningCount={runningTotal}
        installedCount={installedTotal}
        browserCount={browserTotal}
      />

      {/*
        Three stacked tables, in the pre-conversion order — Running Now,
        Installed Tools, Browser History. No `onUserClick`: every row already
        belongs to this user, so the user column is dropped and the cells are
        plain text rather than links that go nowhere.
      */}
      <DataTableRunningNowContent
        organizationId={organizationId}
        scopeUserId={userId}
        tableId={TABLE_IDS.ORGANIZATION.SHADOW_AI_USER_SCOPE.RUNNING}
        initialState={runningInitialState}
      />

      <InstalledToolsSection
        organizationId={organizationId}
        scopeUserId={userId}
        tableId={TABLE_IDS.ORGANIZATION.SHADOW_AI_USER_SCOPE.TOOLS}
        initialState={installedInitialState}
      />

      <DataTableBrowserAiToolsContent
        organizationId={organizationId}
        scopeUserId={userId}
        tableId={TABLE_IDS.ORGANIZATION.SHADOW_AI_USER_SCOPE.BROWSER}
        openDomainOnLoad={deepLinkedDomain}
        title="Browser History"
      />
    </div>
  );
}
